// @flow
import {
  call,
  takeEvery,
  put,
  select,
  race,
} from 'redux-saga/effects';
import { normalize } from 'normalizr';
import { delay } from 'redux-saga';
import * as reactRouter from 'react-router-redux';
import moment from 'moment';
import groupBy from 'lodash/groupBy';
import i18n from 'i18n-js';

import { REQUEST_TIMEOUT, DEFAULT_SOCKET_ROLE } from '../settings';
import { throwTimeout } from '../lib/http';
import { fibonacciBackoff } from '../lib/retry';
import { transformDateStringToDaySeconds } from '../utils/time';
import {
  TRIPS_FETCH_STARTED,
  TRIPS_SCHOOL_FETCH_STARTED,
  TRIP_CREATION_STARTED,
  TAKE_TRIP_ASSISTANCE_STARTED,
  TRIP_STOP_COMPLETED,
  TRIP_FETCH_STARTED,
  TRIP_COMPLETED, SINGLE_TRIP_FETCH_STARTED,
  FINISH_TRIP_STARTED,
} from '../types/trips';
import * as selectors from '../reducers';
import * as schoolActions from '../actions/schools';
import * as flashMessagesActions from '../actions/flashMessages';
import * as temporalBannersActions from '../actions/temporalBanners';
import * as monitorActions from '../actions/monitors';
import * as busActions from '../actions/buses';
import * as tripActions from '../actions/trips';
import * as studentActions from '../actions/students';
import * as routeActions from '../actions/routes';
import * as socketActions from '../actions/socket';

import {
  Monitors,
  Routes,
  Trips,
  TripStops,
  School,
} from '../api';
import { arrayOfTrips, trip } from '../api/schemas/trips';

function* processTrip({
  userId,
  token,
  routes = {},
  monitors = {},
  buses = {},
  tripStops = {},
  trips = {},
  tripsResult,
}: {
  userId: string,
  token: string,
  routes: Object,
  monitors: Object,
  buses: Object,
  tripStops: Object,
  trips: Object,
  tripsResult: Array<number>
}) {
  // Register routes

  // yield put(routeActions.fetchRoutesSucceed(
  //   routes,
  //   Object.keys(routes).map(elm => parseInt(elm, 10)),
  // ));

  // Register monitors
  yield put(monitorActions.registerMonitors(monitors));

  // For each trip stop
  const processedTripStops = {};
  Object.keys(tripStops).forEach((tripStopId) => {
    const { eta_distance, ...tripStop } = tripStops[tripStopId];
    const {
      time = '0',
      is_eta = false,
      distance = 0,
    } = eta_distance != null
      ? eta_distance
      : {};

    processedTripStops[tripStopId] = {
      ...tripStop,
      eta: is_eta ? transformDateStringToDaySeconds(`${time.replace(' ', 'T')}`) : undefined,
      eta_delta: !is_eta ? time : undefined,
      distance,
    };
  });
  yield put(tripActions.succeedTripStopsFetch(processedTripStops));

  // Register trips
  const processedTrips = {};
  Object.keys(trips).forEach((tripId) => {
    const {
      derived_from,
      stops,
      bus,
      last_position,
      ...trip
    } = trips[tripId];
    const { route_type, school } = routes[derived_from];

    // Set the bus location to the location of the first stop.
    // It's not bad idea to mutate buses because it's not in the state yet.
    let firstStopLocation;
    if (stops && stops.length > 0) {
      firstStopLocation = tripStops[stops[0]].point;
    }
    buses[bus].location = {
      latitude: firstStopLocation.latitude + 0.00001,
      longitude: firstStopLocation.longitude + 0.00001,
    };
    if (last_position) {
      buses[bus] = {
        ...buses[bus],
        location: {
          latitude: last_position.latitude,
          longitude: last_position.longitude,
        },
        lastConnection: Date.now(),
        isOffline: false,
      }
    }
    // Build modified trip
    processedTrips[trip.id] = {
      ...trip,
      trip_type: route_type,
      route: derived_from,
      stops,
      school,
      bus,
    };
  });
  // Register trips

  yield put(busActions.registerBuses(buses));
  for (let i = 0; i < Object.keys(trips).length; i += 1) {
    const tripId = Object.keys(trips)[i];
    // Register buses
    yield put(busActions.updateBus(trips[tripId].bus, buses[trips[tripId].bus].location));
  }

  yield put(tripActions.fetchTripsSucceed(processedTrips, tripsResult));

  // Connect to trip channels
  for (let current = 0; current < tripsResult.length; current += 1) {
    const tripId = tripsResult[current];
    yield put(socketActions.join(
      userId,
      token,
      DEFAULT_SOCKET_ROLE,
      `trip:${tripId}`,
    ));
  }
}

function* addETA(tripStops) {
  const groupedTs = groupBy(tripStops, element => element.trip);
  const processedTripStops = tripStops;
  Object.values(groupedTs).forEach(
    (element) => {
      const orderedTs = Object.values(element).sort((a, b) => a.ordinal - b.ordinal);
      if (orderedTs[0].eta_distance) {
        const firstETA = moment(orderedTs[0].eta_distance.time).add(-6, 'hours');
        orderedTs.forEach((ts, index) => {
          const eta_temp = moment(orderedTs[0].eta_distance.time).add(-6, 'hours');
          let eta = 0;
          if (ts.ordinal > 0) {
            for (let tsi = 0; tsi < index; tsi += 1) {
              eta_temp.add(orderedTs[tsi].eta_distance.time, 'seconds');
            }
            eta = eta_temp.add(ts.eta_distance.time, 'seconds').format('h:mm a');
          }
          eta = eta_temp.format('h:mm a');
          processedTripStops[ts.id] = {
            ...ts,
            eta,
          };
        });
      }
    },
  );
  yield put(tripActions.succeedTripStopsFetch(processedTripStops));
}

function* fetchSchoolTrips(action) {
  const token = yield select(selectors.getToken);
  const { schoolId } = action.payload;
  const userId = yield select(selectors.getUserId);

  try {
    // Fetch current trip
    const { response, timeout } = yield race({
      response: call(
        [School.custom, 'trips'],
        { token, id: schoolId /* timestamp */  },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchSchoolTrips saga');
    }
    // Fetch Trips
    for (let i = 0; i < response.length; i++) {
      const tripId = response[i];
      yield put(tripActions.startFetchSingleTrip(tripId));
    }
    const {
      entities: {
        routes,
        trips,
        buses,
        tripStops,
      },
      result,
    } = normalize(response, arrayOfTrips);


    yield put(routeActions.fetchRoutesSucceed(
      routes,
      Object.keys(routes).map(elm => parseInt(elm, 10)),
    ));
    // Register monitors
    yield put(monitorActions.registerMonitors(monitors));

    // For each trip
    yield (addETA(tripStops));

    // Register trips
    const processedTrips = {};
    const tripIds = [];
    Object.keys(trips).forEach((tripId) => {
      tripIds.push(parseInt(tripId, 10));
      const {
        derived_from,
        stops,
        bus,
        last_position,
        ...tripRest
      } = trips[tripId];
      const { route_type } = routes[derived_from];
      // Set the bus location to the location of the first stop.
      // It's not bad idea to mutate buses because it's not in the state yet.
      let firstStopLocation;
      if (stops && stops.length > 0) {
        firstStopLocation = tripStops[stops[0]].point;
      }

      buses[bus].location = firstStopLocation;
      if (last_position) {
        buses[bus].location = last_position;
      }
      buses[bus].trip = parseInt(tripId, 10);

      processedTrips[tripRest.id] = {
        ...tripRest,
        trip_type: route_type,
        route: derived_from,
        stops,
        bus,
      };
    });
    // // Register buses
    yield put(busActions.registerBuses(buses));

    // Register trips
    yield put(tripActions.successFetchSchoolTrips(processedTrips, tripIds));

    // Join to trip channel
    for (let i = 0; i < result.length; i += 1) {
      yield put(socketActions.join(
        userId,
        token,
        DEFAULT_SOCKET_ROLE,
        `trip:${result[i]}`,
      ));
    }
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(tripActions.failCurrentTripFetch({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* fetchCurrentTrip(action) {
  const token = yield select(selectors.getToken);
  const monitor = yield select(selectors.getProfileOfType, 'monitor');
  const userId = yield select(selectors.getUserId);

  try {
    // Fetch current trip
    const {response, timeout} = yield race({
      response: call(
        [Monitors.custom, 'currentTrip'],
        { token, id: monitor.id /* timestamp */ },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchCurrentTrip saga');
    }

    const {
      entities: {
        routes,
        trips,
        schools,
        monitors,
        buses,
        tripStops,
      },
      result,
    } = normalize(response, trip);

    // Register school
    yield put(schoolActions.registerSchools(schools));

    // Register monitors
    yield put(monitorActions.registerMonitors(monitors));

    // For each trip
    yield put(tripActions.succeedTripStopsFetch(tripStops));

    // Register trips
    const processedTrips = {};
    Object.keys(trips).forEach((tripId) => {
      const {
        derived_from,
        stops,
        bus,
        ...tripRest
      } = trips[tripId];
      const { route_type } = routes[derived_from];

      // Set the bus location to the location of the first stop.
      // It's not bad idea to mutate buses because it's not in the state yet.
      let firstStopLocation;
      if (stops && stops.length > 0) {
        firstStopLocation = tripStops[stops[0]].point;
      }
      buses[bus].location = firstStopLocation;

      processedTrips[tripRest.id] = {
        ...tripRest,
        trip_type: route_type,
        route: derived_from,
        stops,
        bus,
      };
    });

    // Register buses
    yield put(busActions.registerBuses(buses));

    // Register trips
    yield put(tripActions.successFetchCurrentTrip(processedTrips));

    // Set current trip
    yield put(tripActions.setCurrentTrip(result));

    // Start student fetching
    yield put(studentActions.startFetchStudents());

    // Join to trip channel
    const { has_took_assistance = false } = trips[result] || {};
    if (has_took_assistance) {
      yield put(socketActions.join(
        userId,
        token,
        DEFAULT_SOCKET_ROLE,
        `trip:${result}`,
      ));
    }
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(tripActions.failCurrentTripFetch({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}


function* createTrip(action) {
  const token = yield select(selectors.getToken);
  const currentRoute = yield select(selectors.getCurrentRoute);

  try {
    // Create new trip
    const { timeout } = yield race({
      response: call(
        [Routes.custom, 'createTrip'],
        { token, id: currentRoute.id, data: {/* timestamp */} },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('createTrip saga');
    }

    // Update route to tell that there is currently a trip
    yield put(routeActions.updateRoute(
      currentRoute.id,
      { has_trip: true },
    ));

    // Start fetching new current trip
    yield put(tripActions.startFetchCurrentTrip());
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(tripActions.failTripCreation({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* saveAssistance(action) {
  const token = yield select(selectors.getToken);
  const userId = yield select(selectors.getUserId);
  const currentTripId = -1; // yield select(selectors.getCurrentTrip);
  const studentAssistance = yield select(selectors.getTripStudentAssistance, currentTripId);

  try {
    // Save trip assistance
    const { timeout } = yield race({
      response: call(
        [Trips.custom, 'saveAssistance'],
        {
          token,
          id: currentTripId,
          data: {
            /* timestamp */
            student_assistance: studentAssistance,
          },
        },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('saveAssistance saga');
    }

    // Update route to tell that there is currently a trip
    yield put(tripActions.updateTrip(
      currentTripId,
      { has_took_assistance: true },
    ));

    // Connect to socket channel
    yield put(socketActions.join(
      userId,
      token,
      DEFAULT_SOCKET_ROLE,
      `trip:${currentTripId}`,
    ));

    // Mark as successful
    yield put(tripActions.succeedTakeTripAssistance());
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(tripActions.failTakeTripAssistance({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* tryToFetchTrip(action) {
  const token = yield select(selectors.getToken);
  const userId = yield select(selectors.getUserId);
  const id = action.payload;

  const tripObject = yield select(selectors.getTrip, id);

  if (typeof tripObject === 'undefined') {
    const mock = {
      delay: 0,
      response: {
        status: 200,
        statusText: 'ok',
        body: {},
      },
    };

    try {
      const { response, timeout } = yield race({
        response: call([Trips, 'detail'], { token, id, mock }),
        timeout: call(delay, REQUEST_TIMEOUT),
      });

      if (timeout) {
        throwTimeout('tryToFetchTrip saga');
      }

      const {
        entities: {
          trips, monitors, buses, tripStops, routes,
        },
        result,
      } = normalize(response, trip);

      yield processTrip({
        userId,
        token,
        routes,
        monitors,
        buses,
        tripStops,
        trips,
        tripsResult: [result],
      });

      yield put(flashMessagesActions.shout(i18n.t('flashMessages.tripStarted', { Route: routes[trips[id].derived_from].short_name }), 2000, [0, 1, 2, 3, 4], {}));
    } catch (error) {
      const {
        status, statusText, data, plain,
      } = error;

      // Fetch error
      yield put(tripActions.fetchTripFailed({
        object_id: id,
        status,
        statusText,
        data: plain ? 'Error en el servidor' : data,
        retryAction: action,
        message: '',
        statusCode: 400,
      }));
    }
  }
}

function* fetchTrip(action) {
  const token = yield select(selectors.getToken);
  const userId = yield select(selectors.getUserId);
  const id = action.payload;

  const tripObject = yield select(selectors.getTrip, id);

  if (typeof tripObject === 'undefined') {
    const mock = {
      delay: 0,
      response: {
        status: 200,
        statusText: 'ok',
        body: {},
      },
    };

    try {
      const { response, timeout } = yield race({
        response: call([Trips, 'detail'], { token, id, mock }),
        timeout: call(delay, REQUEST_TIMEOUT),
      });

      if (timeout) {
        throwTimeout('tryToFetchTrip saga');
      }

      const {
        entities: {
          trips, monitors, buses, tripStops, routes,
        },
        result,
      } = normalize(response, trip);

      yield processTrip({
        userId,
        token,
        routes,
        monitors,
        buses,
        tripStops,
        trips,
        tripsResult: [result],
      });
    } catch (error) {
      const {
        status, statusText, data, plain,
      } = error;

      // Fetch error
      yield put(tripActions.fetchTripFailed({
        object_id: id,
        status,
        statusText,
        data: plain ? 'Error en el servidor' : data,
        retryAction: action,
        message: '',
        statusCode: 400,
      }));
    }
  }
}

function* completeTripStop(action) {
  const { payload } = action;
  const token = yield select(selectors.getToken);
  const { id } = payload;
  const tripStop = yield select(selectors.getTripStop, id);
  const tripObj = yield select(selectors.getTrip, tripStop.trip);
  const route = yield select(selectors.getRoute, tripObj.route);
  const nextDestiny = yield select(selectors.getNextTripStop, tripStop.trip);

  // If trip completed!
  if (typeof nextDestiny === 'undefined') {
    yield put(tripActions.closeTrip(tripStop.trip));
    yield put(tripActions.completeTrip(tripStop.trip));
  }
  if (route) {
    yield put(flashMessagesActions.shout(i18n.t('flashMessages.busArrived', {
      Route: route.short_name,
      Stop: tripStop.name,
    }), 2000, [], {}));
  }

  // Notify django of trip stop completion
  yield put(tripActions.updateTripStop(id, { is_completed: true }));
  try {
    yield call(
      fibonacciBackoff,
      [TripStops, 'update'],
      { token, id, data: { is_completed: true } },
    );
  } catch (e) { /* noop */
  }
}

function* handleTripCompletion(action) {
  const token = yield select(selectors.getToken);
  const userId = yield select(selectors.getUserId);
  const { id } = action.payload;
  const tripObject = yield select(selectors.getTrip, id);
  const route = yield select(selectors.getRoute, tripObject.route);

  // Notify elixir of the trip completion
  yield put(socketActions.push(`trip:${id}`, 'trip:complete'));

  // Disconnect from trip channel
  yield put(socketActions.disjoin(userId, `trip:${id}`));

  // Remove trip from state.
  yield put(tripActions.removeTrip(id));

  // Remove Bus
  yield put(busActions.removeBus(tripObject.bus));

  yield put(flashMessagesActions.shout(i18n.t('flashMessages.tripFinished', { Route: route ? route.short_name : '' }), 2000, [0, 1, 2, 3], {}));
  try {
    yield call(
      fibonacciBackoff,
      [Trips, 'update'],
      { token, id, data: { is_completed: true } },
    );
  } catch (e) { /* noop */ }
}

function* finishTrip(action) {
  const { payload } = action;
  const token = yield select(selectors.getToken);

  try {
    yield call(
      [Trips.custom, 'completeTrip'],
      {
        token,
        id: payload,
      },
    );

    yield put(tripActions.finishTripSucceed());
    yield put(tripActions.removeTrip(payload));
    yield put(temporalBannersActions.shout(i18n.t('common.finishTripSucceedMessage'), 3000, 1, {}));
    yield put(reactRouter.push('/admin/backend'));
    yield put(reactRouter.push('/admin/map'));
  } catch (error) {
    yield put(tripActions.finishTripFailed(error));
    yield put(temporalBannersActions.shout(i18n.t('common.finishTripFailedMessage'), 3000, -1, {}));
  }
}

export function* watchFinishTrip(): Iterator<any> {
  yield takeEvery(
    FINISH_TRIP_STARTED,
    finishTrip,
  );
}

export function* watchTripsSchoolFetch(): Iterator<any> {
  yield takeEvery(
    TRIPS_SCHOOL_FETCH_STARTED,
    fetchSchoolTrips,
  );
}

export function* watchCurrentTripFetch(): Iterator<any> {
  yield takeEvery(
    TRIPS_FETCH_STARTED,
    fetchCurrentTrip,
  );
}

export function* watchCreateTrip(): Iterator<any> {
  yield takeEvery(
    TRIP_CREATION_STARTED,
    createTrip,
  );
}

export function* watchTakeTripAssistance(): Iterator<any> {
  yield takeEvery(
    TAKE_TRIP_ASSISTANCE_STARTED,
    saveAssistance,
  );
}

export function* watchTripStopCompleted(): Iterator<any> {
  yield takeEvery(
    TRIP_STOP_COMPLETED,
    completeTripStop,
  );
}

export function* watchTripCompletion(): Iterator<any> {
  yield takeEvery(
    TRIP_COMPLETED,
    handleTripCompletion,
  );
}

export function* watchTripFetchStarted(): Iterator<any> {
  yield takeEvery(
    TRIP_FETCH_STARTED,
    tryToFetchTrip,
  );
}

export function* watchSingleTripFetchStarted(): Iterator<any> {
  yield takeEvery(
    SINGLE_TRIP_FETCH_STARTED,
    fetchTrip,
  );
}



// WEBPACK FOOTER //
// ./src/scripts/sagas/trips.js